import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { formatJson, type Indent, minifyJson } from "../lib/json";

const SAMPLE =
  '{"name":"Toolverse","tools":16,"free":true,"tags":["json","format"]}';

const selectClass =
  "h-9 rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function tabClass(active: boolean) {
  return `rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
    active
      ? "bg-primary text-primary-foreground"
      : "text-muted-foreground hover:text-foreground"
  }`;
}

export function JsonFormatter() {
  const uid = useId();
  const [input, setInput] = useState(SAMPLE);
  const [mode, setMode] = useState<"pretty" | "minify">("pretty");
  const [indent, setIndent] = useState<Indent>("2");
  const [copied, setCopied] = useState(false);

  const result = useMemo(
    () => (mode === "pretty" ? formatJson(input, indent) : minifyJson(input)),
    [input, mode, indent],
  );

  const copy = async () => {
    if (!result.output) return;
    try {
      await navigator.clipboard.writeText(result.output);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <Label htmlFor={`${uid}-in`}>{m.jf_input_label()}</Label>
        <textarea
          id={`${uid}-in`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={m.jf_placeholder()}
          spellCheck={false}
          className="min-h-[10rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
        />
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex gap-1 rounded-lg border border-border p-1">
            <button
              type="button"
              aria-pressed={mode === "pretty"}
              onClick={() => setMode("pretty")}
              className={tabClass(mode === "pretty")}
            >
              {m.jf_beautify()}
            </button>
            <button
              type="button"
              aria-pressed={mode === "minify"}
              onClick={() => setMode("minify")}
              className={tabClass(mode === "minify")}
            >
              {m.jf_minify()}
            </button>
          </div>
          {mode === "pretty" && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              {m.jf_indent()}
              <select
                className={selectClass}
                value={indent}
                onChange={(e) => setIndent(e.target.value as Indent)}
                aria-label={m.jf_indent()}
              >
                <option value="2">{m.jf_indent_2()}</option>
                <option value="4">{m.jf_indent_4()}</option>
                <option value="tab">{m.jf_indent_tab()}</option>
              </select>
            </label>
          )}
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {m.jf_output_label()}
          </h2>
          {result.ok && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copy}
              className="gap-2"
            >
              {copied ? (
                <Check className="h-4 w-4 text-emerald-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              {copied ? m.jf_copied() : m.jf_copy()}
            </Button>
          )}
        </div>
        {result.ok ? (
          <textarea
            readOnly
            data-testid="jf-output"
            aria-label={m.jf_output_label()}
            value={result.output}
            spellCheck={false}
            className="min-h-[10rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        ) : (
          <p
            data-testid="jf-error"
            className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 font-mono text-sm text-rose-300"
          >
            {result.error?.message}
            {result.error?.line != null && (
              <span>
                {" — "}
                {m.jf_error_location({
                  line: String(result.error.line),
                  column: String(result.error.column ?? 0),
                })}
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
